import React, { FC, forwardRef, useMemo } from 'react';
import { AgencyAccountType, AuthAgencyAccountType } from 'wb-reddit/app/routes/advertisers/utils';
import {
	alephToast,
	BanIcon,
	Button, ButtonColor,
	CloseIcon,
	Dialog,
	LoaderBars,
	NotificationToast,
	NotificationType,
	TrashCanIcon,
	useOpen,
	isReddit,
} from '@aleph/shared-components';
import AccessBadge from 'wb-reddit/app/common/components/accessBadge';
import { useMutation } from '@apollo/client';
import {
	DeleteAgencyAccountDocument,
	RedditAdvertiserListDocument,
	RedditAdvertiserListQueryItems,
	UserAccountLevel,
} from 'wb-reddit/graphql/generated';
import AccessImages from 'wb-reddit/app/routes/reports/components/accessImages';
import { RedditAccessEntity } from 'wb-reddit/app/routes/reports/utils';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import useUserMetaData from 'wb-reddit/app/common/hooks/useUserMetaData';
import { VARIABLES } from 'wb-reddit/styles/variables';
import EntityName from 'wb-reddit/app/common/components/EntityName';
import { HandleUpdateAdvertiserRow } from 'wb-reddit/app/routes/advertisers/container';
import { Trans, useTranslation } from 'react-i18next';
import {
	AuthGraphAdvertiserListQueryItems,
} from 'wb-spotify/graphql/authgraph/generated';
import useSpotifyRolePermissions from 'wb-spotify/app/common/hooks/useSpotifyPermissions';

interface IProps {
	advertiser: AgencyAccountType | AuthAgencyAccountType;
	deleteRow: HandleUpdateAdvertiserRow
}

const AdvertiserListItem: FC<IProps> = forwardRef<HTMLDivElement, IProps>(({ advertiser, deleteRow, ...rest }, ref) => {
	const { t } = useTranslation();

	const [open, { onOpen, onClose }] = useOpen(false);
	const navigate = useNavigate();
	const [deleteAdvertiser, { loading: isDeleting }] = useMutation(DeleteAgencyAccountDocument, {
		errorPolicy:         'all',
		refetchQueries:      [RedditAdvertiserListDocument],
		awaitRefetchQueries: true,
	});

	const agencyOwner = isReddit()
		? (advertiser as RedditAdvertiserListQueryItems)?.users?.find(user => user?.agencyIsOwner)?.user
		: (advertiser as AuthGraphAdvertiserListQueryItems)?.owner;

	const domain = useMemo(() => {
		// @ts-ignore
		const prop = isReddit() ? agencyOwner?.userEmail : advertiser?.owner?.userEmail;

		return prop?.split('@')[1];
	}, [advertiser, agencyOwner]);

	// TODO provide for spotify when BE is ready with delete status
	const isApproved = (advertiser as RedditAdvertiserListQueryItems)?.agencyAccountActivatedAt !== null;

	const platformUser = useUserMetaData();

	const { canDeleteAdvertiserAccount, canEditAdvertiserAccount } = useSpotifyRolePermissions();

	const canDelete = useMemo(() => {
		if (isReddit()) {
			return agencyOwner?.userId === platformUser?.userId
				|| platformUser?.userAccountLevel === UserAccountLevel.Admin;
		}

		// return canDeleteAdvertiserAccount;
		// On auth graph there is no delete mutation, for now disable delete button enable after its implemented
		return false;
	}, [agencyOwner?.userId, platformUser?.userId, platformUser?.userAccountLevel, canDeleteAdvertiserAccount]);

	const startEditingAdvertiser = () => {
		navigate(`/advertisers/${advertiser.agencyAccountId}`);
	};

	const access = useMemo(
		() => (isReddit() ? ([
			...((advertiser as RedditAdvertiserListQueryItems)?.userGroups ?? []),
			...(((advertiser as RedditAdvertiserListQueryItems)?.users ?? [])
				?.filter(user => !user?.agencyIsOwner)
				?.map(user => user?.user) ?? []),
		]) as RedditAccessEntity[]
			: (advertiser as AuthGraphAdvertiserListQueryItems)?.advertiserAccountAccess?.map(item => item?.userGroup)) as RedditAccessEntity[],
		[advertiser],
	);

	// TODO BE doesn't have an ad accounts for now
	const accounts = (advertiser as RedditAdvertiserListQueryItems)?.clients?.map(client => ({
		type: undefined,
		name: client.clientName ?? client.adAccountName,
	})) ?? [];

	const salesforceId = (advertiser as RedditAdvertiserListQueryItems)?.salesforceId;

	// TODO provide condition when mutation is provided
	const disabledDelete = isReddit() ? ((advertiser as RedditAdvertiserListQueryItems)?.clients ?? []).length > 0 : true;

	return (
		<div
			className="flex items-center h-[82px] whitespace-nowrap border-b border-b-200"
			{...rest}
			ref={ref}
			data-testid={`advertiser-${advertiser.id}`}
		>
			<EntityName
				onClick={canEditAdvertiserAccount ? startEditingAdvertiser : undefined}
				name={advertiser.agencyAccountName}
				entity={advertiser}
			/>
			{isReddit() && (
				<div className="px-[8px] w-[120px] text-gray-950 overflow-hidden overflow-ellipsis">
					<div
						className={
							classNames('rounded-full px-[12px] py-[4px] text-[12px] inline-block', {
								'bg-green-50 text-green-600':   isApproved,
								'bg-yellow-50 text-yellow-600': !isApproved,
							})
						}
					>
						{isApproved ? t('re: advertiser.status.approved', 'Approved') : t('re: advertiser.status.pending', 'Pending')}
					</div>
				</div>
			)}
			<div className="px-[8px] w-[202px] text-gray-600 overflow-hidden overflow-ellipsis">
				@{domain}
			</div>
			<div className="px-[8px] w-[168px]">
				<AccessImages
					accessEntities={access}
					width={168}
				/>
			</div>
			<div className="flex px-[8px] w-[116px]">
				<AccessBadge
					accessEntities={accounts}
					label="Grid view"
					labelClassName='min-w-[68px] text-[12px]'
					href={`/superGrid/(filters:(agencyAccountId:((value:(${advertiser.id})))))`}
				/>
			</div>
			{isReddit() && (
				<div
					className="px-[8px] w-[90px] text-[12px]"
					data-testid="advertiser-profile-button"
				>
					{salesforceId ? <a
						href={`https://alephholding.my.salesforce.com/${salesforceId}`}
						target="_blank"
						className="text-blue hover:text-blue-600"
						rel="noreferrer"
					>
						{t('re: advertiser.open_profile', 'Open profile')}
					</a>
						: <span className="text-gray-600 cursor-disabled">{t('re: advertiser.open_profile', 'Open profile')}</span>
					}
				</div>
			)}
			<div className="w-[56px] flex items-center justify-center">
				{canDelete ? <div className="flex items-center">
					{
						isDeleting ? <Button
							leftNode={
								<LoaderBars
									className="animate-spin"
								/>
							}
							disabled
						/>
							: <Button
								leftNode={<TrashCanIcon />}
								appearance="ghost"
								disabled={disabledDelete}
								loading={isDeleting}
								onClick={onOpen}
								active={open}
								color={ButtonColor.RED}
								data-testid="advertiser-delete-button"
							/>
					}
				</div> : <div />
				}
			</div>
			<Dialog
				className='w-[548px]'
				onClose={onClose}
				isOpen={open}
			>
				<Dialog.Header preset={{
					prefix: <TrashCanIcon
						strokeWidth={1.3}
					/>,
					title:  t('re: advertiser.delete_advertiser.button', 'Delete advertiser'),
					suffix: <Button
						className="ml-auto"
						appearance="ghost"
						color="gray"
						rightNode={<CloseIcon
							width={14}
							height={14}
							fill={VARIABLES.COLORS.GRAY[950]}
						/>}
						onClick={onClose}
					/>,
				}}
				/>
				<Dialog.Content>
					<section className="text-gray-800 font-[500]">
						<div className="text-[16px] leading-[20px]">
							{t('re: advertiser.delete_advertiser.title', 'Are you sure you want to delete advertiser')} <span
								className="font-[600]"
							>{advertiser.agencyAccountName}</span>?
						</div>
						<div className="text-[12px] leading-[20px] pt-[20px]">
							{t('re: advertiser.delete_advertiser.body', 'Deleting an advertiser will also delete its historical campaign data and disconnect associated Reddit ad accounts.')}
						</div>
					</section>
				</Dialog.Content>
				<Dialog.Footer>
					<Button
						title={t('sc: cancel', 'Cancel')}
						leftNode={<BanIcon />}
						appearance="outlined"
						color="gray"
						onClick={() => {
							onClose();
						}}
					/>

					<Button
						title={t('sc: delete', 'Delete')}
						leftNode={<TrashCanIcon />}
						color="red"
						onClick={async () => {
							onClose();
							const { errors } = await deleteAdvertiser({
								variables: {
									agencyAccountId: advertiser.agencyAccountId,
								},
							});

							if (errors) {
								alephToast(
									<NotificationToast
										type={NotificationType.Error}
										title={t('sc: action err', { action: 'deleting the advertiser' })}
									/>,
								);

								return;
							}

							deleteRow?.({
								// @ts-ignore TODO provide type to parrent func when mutation is provided
								row:      advertiser,
								isDelete: true,
							});

							alephToast(
								<NotificationToast
									type={NotificationType.Success}
									title={
										<div>
											<Trans
												i18nKey="re: advertiser.delete_advertiser.success"
												defaults="Advertiser <strong>{{advertiser}}</strong> deleted successfully."
												values={{ advertiser: advertiser.agencyAccountName }}
											>
												Advertiser <strong>{advertiser.agencyAccountName}</strong> deleted successfully.
											</Trans>
										</div>
									}
								/>,
							);
						}}
					/>
				</Dialog.Footer>
			</Dialog>
		</div>
	);
});

export default AdvertiserListItem;
